// Dashboard de indicadores de gestión de nómina. Nómina → Resumen.
// RPC nom_dashboard (script 167). Paleta categórica validada (dataviz):
// teal #0E9488 / violeta #7C5CDB / coral #E05A3A sobre superficie clara.
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import {
  AlertTriangle, ArrowDownRight, ArrowUpRight, Banknote, CalendarClock, HandCoins,
  Inbox, Loader2, Umbrella, Users, Wallet,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { MESES, TIPOS_CONTRATO, fieldCls, money, nomRpc, TIPOS_ROL } from './shared';
import { EstadoRol } from './Rol';
import { useThemeStore } from '@/stores/theme.store';

const TEAL = '#0E9488';
const VIOLETA = '#7C5CDB';
const CORAL = '#E05A3A';
// versión neón para el tema Stark (mismos hues validados, más brillantes)
const STARK = { teal: '#22d3ee', violeta: '#a970ff', coral: '#ff7a66' };
const HOY = new Date();

interface DashData {
  sbu: number | null;
  headcount: {
    activos: number; mujeres: number; hombres: number; discapacidad: number; discapacidad_pct: number;
    altas_mes: number; bajas_mes: number; parcial: number; teletrabajo: number;
  };
  contratos_por_tipo: { tipo: string; n: number }[];
  rol: { estado: string; n_empleados: number; total_ingresos: number; total_egresos: number; total_neto: number; neto_promedio: number } | null;
  roles_por_tipo: { tipo: string; codigo: string; estado: 'borrador' | 'cerrado' | 'pagado'; n_empleados: number; total_ingresos: number; total_egresos: number; total_neto: number }[];
  costo: { remuneraciones: number; provisiones: number; patronal: number };
  por_departamento: { departamento: string; n: number; ingresos: number }[];
  egresos_por_rubro: { rubro_codigo: string; descripcion: string; total: number }[];
  serie: { anio: number; mes: number; estado: string; ingresos: number; neto: number }[];
  movimientos: {
    h_supl: number; h_extra: number; h_noct: number; faltas_dias: number;
    anticipos_pendientes: number; anticipos_monto: number;
    prestamos_activos: number; prestamos_saldo: number; cuotas_iess: number;
  };
  vacaciones: { dias_pendientes: number; acumuladores: number };
  alertas: { bajo_sbu: number; contratos_por_vencer: number; prueba_por_vencer: number; solicitudes_pendientes: number; sin_parametros: boolean };
}

function Kpi({ icon, label, valor, detalle }: { icon: React.ReactNode; label: string; valor: string; detalle?: string }) {
  return (
    <Card><CardContent className="py-3 flex items-start gap-3">
      <div className="text-primary mt-0.5">{icon}</div>
      <div className="min-w-0">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="text-lg font-semibold font-mono tabular-nums truncate">{valor}</div>
        {detalle && <div className="text-xs text-muted-foreground">{detalle}</div>}
      </div>
    </CardContent></Card>
  );
}

function Alerta({ n, texto, ok }: { n: number | boolean; texto: string; ok?: string }) {
  const activa = typeof n === 'boolean' ? n : n > 0;
  return (
    <div className={`flex items-center gap-2 text-sm px-3 py-2 rounded-md ${activa ? 'bg-pendiente-soft text-pendiente' : 'bg-estable-soft text-estable'}`}>
      <AlertTriangle className={`w-4 h-4 ${activa ? '' : 'opacity-40'}`} />
      <span>{activa ? texto : (ok ?? texto)}</span>
      {typeof n === 'number' && <span className="ml-auto font-mono font-semibold">{n}</span>}
    </div>
  );
}

export function DashboardNomina() {
  const [anio, setAnio] = useState(HOY.getFullYear());
  const [mes, setMes] = useState(HOY.getMonth() + 1);
  const isStark = useThemeStore((st) => st.currentThemeId === 'stark-hud');
  const cTeal = isStark ? STARK.teal : TEAL;
  const cVioleta = isStark ? STARK.violeta : VIOLETA;
  const cCoral = isStark ? STARK.coral : CORAL;
  const { data, isFetching } = useQuery<DashData>({
    queryKey: ['nom-dashboard', anio, mes],
    queryFn: () => nomRpc<DashData>('nom_dashboard', { _anio: anio, _mes: mes }),
    staleTime: 0,
  });

  const costoTotal = data ? Number(data.costo.remuneraciones) + Number(data.costo.provisiones) + Number(data.costo.patronal) : 0;
  const serie = (data?.serie ?? []).map((s) => ({ ...s, etiqueta: `${MESES[s.mes - 1].slice(0, 3)} ${String(s.anio).slice(2)}` }));
  const tipos = (data?.contratos_por_tipo ?? []).map((t) => ({ ...t, nombre: (TIPOS_CONTRATO[t.tipo] ?? t.tipo).split(' (')[0] }));
  const egresos = (data?.egresos_por_rubro ?? []).map((e) => ({ ...e, nombre: e.descripcion.split(' (')[0] }));

  return (
    <div className="p-6 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold">Dashboard · Nómina</h1>
        <div className="flex items-center gap-2">
          <select className={`${fieldCls} w-36`} value={mes} onChange={(e) => setMes(Number(e.target.value))} aria-label="Mes">
            {MESES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
          <input type="number" className={`${fieldCls} w-24`} value={anio} onChange={(e) => setAnio(Number(e.target.value))} aria-label="Año" />
          {isFetching && <Loader2 className="w-4 h-4 animate-spin text-primary" />}
        </div>
      </div>

      {data && (
        <>
          {/* KPIs */}
          <div className="grid gap-3 grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
            <Kpi icon={<Users className="w-5 h-5" />} label="Empleados activos" valor={String(data.headcount.activos)}
                 detalle={`${data.headcount.mujeres} M · ${data.headcount.hombres} H`} />
            <Kpi icon={<ArrowUpRight className="w-5 h-5" />} label="Altas del mes" valor={String(data.headcount.altas_mes)}
                 detalle={`${data.headcount.parcial} parcial · ${data.headcount.teletrabajo} teletrabajo`} />
            <Kpi icon={<ArrowDownRight className="w-5 h-5" />} label="Bajas del mes" valor={String(data.headcount.bajas_mes)} />
            <Kpi icon={<Wallet className="w-5 h-5" />} label="Neto del rol" valor={data.rol ? money(data.rol.total_neto) : '—'}
                 detalle={data.rol ? `${data.rol.estado} · promedio ${money(data.rol.neto_promedio)}` : 'Sin rol del período'} />
            <Kpi icon={<Banknote className="w-5 h-5" />} label="Costo laboral total" valor={data.rol ? money(costoTotal) : '—'}
                 detalle={data.rol ? `+${money(data.costo.provisiones)} prov. · +${money(data.costo.patronal)} patronal` : undefined} />
            <Kpi icon={<Users className="w-5 h-5" />} label="Inclusión (Art. 42.33)" valor={`${Number(data.headcount.discapacidad_pct)}%`}
                 detalle={`${data.headcount.discapacidad} personas · cuota legal 4%`} />
          </div>

          {/* Alertas de cumplimiento */}
          <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-4">
            <Alerta n={data.alertas.solicitudes_pendientes} texto="Solicitudes del portal por resolver" ok="Sin solicitudes pendientes" />
            <Alerta n={data.alertas.contratos_por_vencer} texto="Contratos a plazo vencen en 30 días" ok="Sin contratos por vencer" />
            <Alerta n={data.alertas.prueba_por_vencer} texto="Períodos de prueba vencen en 15 días (Art. 15)" ok="Sin pruebas por vencer" />
            <Alerta n={data.alertas.bajo_sbu} texto="Sueldos bajo el SBU en jornada completa" ok={`Todos los sueldos ≥ SBU (${data.sbu ? money(data.sbu) : '—'})`} />
          </div>
          {data.alertas.sin_parametros && (
            <div className="text-sm text-urgente">Configura los parámetros del año {anio} (Nómina → Configuración → Parámetros).</div>
          )}

          {data.roles_por_tipo.length > 0 && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Roles del período por tipo</CardTitle></CardHeader>
              <CardContent className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b text-left text-xs text-muted-foreground">
                    <th className="py-2 pr-3">Tipo</th><th className="py-2 pr-3">Código</th><th className="py-2 pr-3">Estado</th>
                    <th className="py-2 pr-3 text-right">Empleados</th><th className="py-2 pr-3 text-right">Ingresos</th>
                    <th className="py-2 pr-3 text-right">Egresos</th><th className="py-2 pr-3 text-right">Neto</th>
                  </tr></thead>
                  <tbody>
                    {data.roles_por_tipo.map((r) => (
                      <tr key={r.codigo} className="border-b last:border-0">
                        <td className="py-2 pr-3">{TIPOS_ROL[r.tipo] ?? r.tipo}</td>
                        <td className="py-2 pr-3 font-mono">{r.codigo}</td>
                        <td className="py-2 pr-3"><EstadoRol estado={r.estado} /></td>
                        <td className="py-2 pr-3 text-right font-mono tabular-nums">{r.n_empleados}</td>
                        <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(r.total_ingresos)}</td>
                        <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(r.total_egresos)}</td>
                        <td className="py-2 pr-3 text-right font-mono tabular-nums font-semibold">{money(r.total_neto)}</td>
                      </tr>
                    ))}
                    <tr className="font-semibold bg-muted/30">
                      <td className="py-2 pr-3" colSpan={3}>TOTAL DEL PERÍODO</td>
                      <td className="py-2 pr-3 text-right font-mono tabular-nums">{data.roles_por_tipo.reduce((s, r) => s + r.n_empleados, 0)}</td>
                      <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(data.roles_por_tipo.reduce((s, r) => s + Number(r.total_ingresos), 0))}</td>
                      <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(data.roles_por_tipo.reduce((s, r) => s + Number(r.total_egresos), 0))}</td>
                      <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(data.roles_por_tipo.reduce((s, r) => s + Number(r.total_neto), 0))}</td>
                    </tr>
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}

          {/* Gráficos fila 1 */}
          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Masa salarial — últimos 12 roles</CardTitle></CardHeader>
              <CardContent style={{ height: 260 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={serie} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                    <XAxis dataKey="etiqueta" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                    <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" width={70}
                           tickFormatter={(v: number) => `$${(v / 1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v: number, name: string) => [money(v), name]} />
                    <Legend />
                    <Bar dataKey="ingresos" name="Ingresos" fill={cTeal} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="neto" name="Neto pagado" fill={cVioleta} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Masa salarial por departamento (rol del período)</CardTitle></CardHeader>
              <CardContent style={{ height: 260 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.por_departamento} layout="vertical" margin={{ top: 4, right: 24, left: 8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))"
                           tickFormatter={(v: number) => `$${(v / 1000).toFixed(0)}k`} />
                    <YAxis type="category" dataKey="departamento" width={160} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                    <Tooltip formatter={(v: number) => [money(v), 'Ingresos']} />
                    <Bar dataKey="ingresos" name="Ingresos" fill={cTeal} radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Gráficos fila 2 */}
          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Contratos vigentes por tipo</CardTitle></CardHeader>
              <CardContent style={{ height: 220 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={tipos} layout="vertical" margin={{ top: 4, right: 32, left: 8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" allowDecimals={false} />
                    <YAxis type="category" dataKey="nombre" width={120} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                    <Tooltip formatter={(v: number) => [v, 'Contratos']} />
                    <Bar dataKey="n" name="Contratos" fill={cVioleta} radius={[0, 4, 4, 0]} label={{ position: 'right', fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Egresos del rol por concepto</CardTitle></CardHeader>
              <CardContent style={{ height: 220 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={egresos} layout="vertical" margin={{ top: 4, right: 24, left: 8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))"
                           tickFormatter={(v: number) => `$${(v / 1000).toFixed(1)}k`} />
                    <YAxis type="category" dataKey="nombre" width={190} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                    <Tooltip formatter={(v: number) => [money(v), 'Total']} />
                    <Bar dataKey="total" name="Total" fill={cCoral} radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Movimientos y vacaciones */}
          <div className="grid gap-3 grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
            <Kpi icon={<CalendarClock className="w-5 h-5" />} label="Horas suplementarias (mes)" valor={`${Number(data.movimientos.h_supl)} h`}
                 detalle={`+${Number(data.movimientos.h_extra)} extra · +${Number(data.movimientos.h_noct)} noct.`} />
            <Kpi icon={<CalendarClock className="w-5 h-5" />} label="Faltas (mes)" valor={`${Number(data.movimientos.faltas_dias)} d`} />
            <Kpi icon={<HandCoins className="w-5 h-5" />} label="Anticipos pendientes" valor={money(data.movimientos.anticipos_monto)}
                 detalle={`${data.movimientos.anticipos_pendientes} solicitudes`} />
            <Kpi icon={<Banknote className="w-5 h-5" />} label="Préstamos por cobrar" valor={money(data.movimientos.prestamos_saldo)}
                 detalle={`${data.movimientos.prestamos_activos} activos`} />
            <Kpi icon={<Banknote className="w-5 h-5" />} label="Cuotas IESS (mes)" valor={money(data.movimientos.cuotas_iess)} />
            <Kpi icon={<Umbrella className="w-5 h-5" />} label="Vacaciones pendientes" valor={`${Number(data.vacaciones.dias_pendientes)} d`}
                 detalle={`${data.vacaciones.acumuladores} con >30 días`} />
          </div>

          {data.alertas.solicitudes_pendientes > 0 && (
            <div className="text-sm text-muted-foreground flex items-center gap-2">
              <Inbox className="w-4 h-4" /> Resuelve las solicitudes del portal en Nómina → Movimientos → Solicitudes del portal.
            </div>
          )}
        </>
      )}
    </div>
  );
}
